import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';

/**
 * Export confirmation modal states
 */
export enum ExportModalState {
  CONFIRMATION = 'confirmation',
  DOWNLOADING = 'downloading'
}

/**
 * Modal component for export confirmation and progress
 * Handles user confirmation and shows export status
 *
 * Features:
 * - Confirmation message
 * - Progress indicator
 * - Success/Error feedback
 * - Confirm/Cancel actions
 *
 * @example
 * ```html
 * <app-export-confirmation-modal
 *   [isOpen]="true"
 *   [state]="modalState"
 *   (confirm)="handleConfirm()"
 *   (cancel)="handleCancel()">
 * </app-export-confirmation-modal>
 * ```
 */
@Component({
  selector: 'app-export-confirmation-modal',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  templateUrl: './export-confirmation-modal.component.html',
  styleUrls: ['./export-confirmation-modal.component.scss']
})
export class ExportConfirmationModalComponent {
  /** Controls modal visibility */
  isOpen = input.required<boolean>();

  /** Current state of the export process */
  state = input<ExportModalState>(ExportModalState.CONFIRMATION);

  /** Event emitted when user confirms export */
  confirm = output();

  /** Event emitted when user cancels or closes modal */
  closeModal = output();

  /** Expose enums to template */
  readonly ExportModalState = ExportModalState;
  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  /**
   * Get title based on current state
   */
  get modalTitle(): string {
    switch (this.state()) {
      case ExportModalState.CONFIRMATION:
        return 'Exportar listado';
      case ExportModalState.DOWNLOADING:
        return 'Exportando';
      default:
        return 'Exportar';
    }
  }

  /**
   * Get message based on current state
   */
  get modalMessage(): string {
    switch (this.state()) {
      case ExportModalState.CONFIRMATION:
        return '¿Desea exportar el listado de órdenes ingresadas?';
      case ExportModalState.DOWNLOADING:
        return 'Descargando...';
      default:
        return '';
    }
  }

  /**
   * Check if buttons should be shown
   */
  get showButtons(): boolean {
    return this.state() === ExportModalState.CONFIRMATION;
  }

  /**
   * Handle confirm button click
   */
  onConfirm(): void {
    this.confirm.emit();
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    this.closeModal.emit();
  }

  /**
   * Handle backdrop click (disabled - modal can only be closed via buttons)
   */
  onBackdropClick(): void {
    // Backdrop click disabled - users must use Cancel button or wait for completion
    // This prevents accidental cancellation during confirmation or download
  }
}
